import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { organizationsApi } from '@/api/organizations';
import type { Organization, OrganizationRole } from '@/api/types';

export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  members: (id: string) => [...organizationKeys.all, id, 'members'] as const,
};

export function useOrganizations(params: { page?: number; pageSize?: number; search?: string } = {}) {
  return useQuery({
    queryKey: [...organizationKeys.lists(), params],
    queryFn: () => organizationsApi.list(params),
  });
}

export function useOrganization(id: string | undefined) {
  return useQuery({
    queryKey: [...organizationKeys.all, id ?? ''] as const,
    queryFn: () => organizationsApi.get(id as string),
    enabled: Boolean(id),
  });
}

export function useCreateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Organization>) => organizationsApi.create(payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.lists() }),
  });
}

export function useUpdateOrganization(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Partial<Organization>) => organizationsApi.update(id, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
  });
}

export function useUploadOrganizationLogo(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => organizationsApi.uploadLogo(id, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
  });
}

export function useDeactivateOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationsApi.deactivate(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.lists() }),
  });
}

/** Permanently deletes the organization and all of its competitions - irreversible, SuperAdmin only. */
export function useRemoveOrganization() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => organizationsApi.remove(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.lists() }),
  });
}

export function useOrganizationMembers(orgId: string) {
  return useQuery({
    queryKey: organizationKeys.members(orgId),
    queryFn: () => organizationsApi.getMembers(orgId),
    enabled: !!orgId,
  });
}

export function useAddOrganizationMember(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { userId: string; role: OrganizationRole }) => organizationsApi.addMember(orgId, payload),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.members(orgId) }),
  });
}

export function useRemoveOrganizationMember(orgId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => organizationsApi.removeMember(orgId, userId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: organizationKeys.members(orgId) }),
  });
}
